import authorizedRequest from "./api";
import { getStoredActiveBusinessId } from "./businessContext";

export const ONBOARDING_TOTAL_STEPS = 8;
export const TEAM_ONBOARDING_TOTAL_STEPS = 9;
export const ONBOARDING_COMPLETED_EVENT = "haastia.pro.onboarding.completed";
// Sent by notifyOnboardingStepsChanged, so the progress widget, which stays
// mounted from page to page, checks again after a save instead of at its next
// mount.
export const ONBOARDING_STEPS_CHANGED_EVENT = "haastia.pro.onboarding.stepsChanged";
// These share the "haastia.pro.onboarding." prefix that clearAuthStorage sweeps on
// logout (see utils/auth.js) — keep new onboarding keys under the same prefix.
const SHARE_STORAGE_KEY = "haastia.pro.onboarding.shareProfile";
const PAYOUT_SKIP_STORAGE_KEY = "haastia.pro.onboarding.payoutsSkipped";
const TEAM_INVITE_SKIP_STORAGE_KEY = "haastia.pro.onboarding.teamInviteSkipped";
const TIME_ZONE_CONFIRM_STORAGE_KEY = "haastia.pro.onboarding.timeZoneConfirmed";

const buildUserScopedStorageKey = (baseKey, userId, businessId = null) => {
  const normalizedUserId = typeof userId === "string" ? userId.trim() : "";
  const normalizedBusinessId = typeof businessId === "string" ? businessId.trim() : "";
  const userScopedKey = normalizedUserId ? `${baseKey}.${normalizedUserId}` : baseKey;
  return normalizedBusinessId ? `${userScopedKey}.${normalizedBusinessId}` : userScopedKey;
};

export const getStoredShareCompletion = (userId, businessId = null) => {
  if (typeof window === "undefined") {
    return false;
  }

  if (!userId) {
    return false;
  }

  try {
    const storageKey = buildUserScopedStorageKey(SHARE_STORAGE_KEY, userId, businessId);
    return window.localStorage.getItem(storageKey) === "true";
  } catch (error) {
    return false;
  }
};

export const storeShareCompletion = (value, userId, businessId = null) => {
  if (typeof window === "undefined") {
    return;
  }

  if (!userId) {
    return;
  }

  try {
    const storageKey = buildUserScopedStorageKey(SHARE_STORAGE_KEY, userId, businessId);
    window.localStorage.setItem(storageKey, value ? "true" : "false");
  } catch (error) {
    // Intentionally ignore storage errors (e.g., private mode)
  }
};

export const getStoredPayoutSkipCompletion = (userId, businessId = null) => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const storageKey = buildUserScopedStorageKey(PAYOUT_SKIP_STORAGE_KEY, userId, businessId);
    return window.localStorage.getItem(storageKey) === "true";
  } catch (error) {
    return false;
  }
};

export const storePayoutSkipCompletion = (value, userId, businessId = null) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const storageKey = buildUserScopedStorageKey(PAYOUT_SKIP_STORAGE_KEY, userId, businessId);
    window.localStorage.setItem(storageKey, value ? "true" : "false");
  } catch (error) {
    // Intentionally ignore storage errors (e.g., private mode)
  }
};

export const getStoredTeamInviteSkip = (userId, businessId = null) => {
  if (typeof window === "undefined" || !userId) {
    return false;
  }

  try {
    return window.localStorage.getItem(
      buildUserScopedStorageKey(TEAM_INVITE_SKIP_STORAGE_KEY, userId, businessId)
    ) === "true";
  } catch (error) {
    return false;
  }
};

export const storeTeamInviteSkip = (value, userId, businessId = null) => {
  if (typeof window === "undefined" || !userId) {
    return;
  }

  try {
    window.localStorage.setItem(
      buildUserScopedStorageKey(TEAM_INVITE_SKIP_STORAGE_KEY, userId, businessId),
      value ? "true" : "false"
    );
  } catch (error) {
    // Intentionally ignore storage errors (e.g., private mode)
  }
};

// Remembers which zone the pro confirmed, so a later change to the business
// zone (from Settings or another device) asks again.
export const getStoredTimeZoneConfirmation = (userId, businessId = null) => {
  if (typeof window === "undefined" || !userId) {
    return "";
  }

  try {
    return window.localStorage.getItem(
      buildUserScopedStorageKey(TIME_ZONE_CONFIRM_STORAGE_KEY, userId, businessId)
    ) || "";
  } catch (error) {
    return "";
  }
};

export const storeTimeZoneConfirmation = (timeZone, userId, businessId = null) => {
  if (typeof window === "undefined" || !userId) {
    return;
  }

  try {
    window.localStorage.setItem(
      buildUserScopedStorageKey(TIME_ZONE_CONFIRM_STORAGE_KEY, userId, businessId),
      timeZone || ""
    );
  } catch (error) {
    // Intentionally ignore storage errors (e.g., private mode)
  }
};

// The business zone for the "Set your business details" step. Members without
// access to business settings get null, which hides the control rather than
// failing the whole checklist.
const fetchBusinessTimeZoneSettings = async ({ activeBusinessId, signal }) => {
  if (!activeBusinessId) {
    return { businessTimeZone: null, canManageBusinessTimeZone: false };
  }

  try {
    const payload = await authorizedRequest({
      url: `/api/businesses/${activeBusinessId}/settings`,
      signal,
    });
    const settings = payload?.settings || payload || {};
    return {
      businessTimeZone: settings.timeZone || null,
      canManageBusinessTimeZone: settings.permissions?.canManageBusinessSettings === true,
    };
  } catch (error) {
    return { businessTimeZone: null, canManageBusinessTimeZone: false };
  }
};

export const updateBusinessTimeZone = async ({ activeBusinessId, timeZone }) => {
  const payload = await authorizedRequest({
    url: `/api/businesses/${activeBusinessId}/settings`,
    method: "patch",
    data: { timeZone },
  });
  return payload?.settings?.timeZone || timeZone;
};

const normalizeBoolean = (value) => Boolean(value === true || value === "true" || value === 1);

const normalizeGuidelinesValue = (data) => {
  const profilePayload = data?.professional ?? data;
  const rawValue =
    profilePayload?.profileGuidelines ??
    profilePayload?.data?.profileGuidelines ??
    profilePayload?.profile?.profileGuidelines ??
    "";

  if (typeof rawValue === "string") {
    return rawValue.trim();
  }

  if (rawValue === null || rawValue === undefined) {
    return "";
  }

  return String(rawValue).trim();
};

const normalizeStringValue = (value) => {
  if (typeof value === "string") {
    return value.trim();
  }

  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim();
};

const hasProfileDescription = (data) => {
  const profilePayload = data?.professional ?? data ?? {};
  const rawDescription =
    profilePayload?.bio ??
    profilePayload?.description ??
    profilePayload?.about ??
    profilePayload?.shortDescription ??
    profilePayload?.data?.bio ??
    profilePayload?.profile?.bio ??
    "";

  return normalizeStringValue(rawDescription).length > 0;
};

const hasBusinessAddress = (data) => {
  const profilePayload = data?.professional ?? data ?? {};
  const location =
    profilePayload?.location ?? data?.location ?? data?.data?.location ?? {};

  const street = normalizeStringValue(
    profilePayload?.businessStreetAddress ??
      location?.fullAddress ??
      profilePayload?.businessAddress ??
      location?.address ??
      location?.streetAddress ??
      location?.street ??
      ""
  );
  const city = normalizeStringValue(
    profilePayload?.businessCity ?? location?.city ?? ""
  );
  const state = normalizeStringValue(
    profilePayload?.businessState ?? location?.state ?? ""
  );
  const zip = normalizeStringValue(
    profilePayload?.businessZip ?? location?.zip ?? location?.postalCode ?? ""
  );

  return Boolean(street && city && state && zip);
};

const normalizeWorkLocationType = (value) => {
  if (typeof value !== "string") return "";
  const normalized = value.trim().toLowerCase();
  if (["at_location", "client_location", "both"].includes(normalized)) {
    return normalized;
  }
  return "";
};

const extractServiceLocationTypes = (servicesPayload) => {
  const services = Array.isArray(servicesPayload)
    ? servicesPayload
    : Array.isArray(servicesPayload?.services)
      ? servicesPayload.services
      : Array.isArray(servicesPayload?.data)
        ? servicesPayload.data
        : [];

  return services
    .map((service) =>
      normalizeWorkLocationType(service?.locationType || service?.serviceLocationType)
    )
    .filter(Boolean);
};

const inferWorkLocationType = (profilePayload, servicesPayload) => {
  const profile = profilePayload?.professional ?? profilePayload ?? {};
  const explicit = normalizeWorkLocationType(
    profile?.workLocationType ??
      profile?.serviceLocationPreference ??
      profile?.data?.workLocationType ??
      profile?.profile?.workLocationType
  );
  if (explicit) return explicit;

  const serviceLocationTypes = extractServiceLocationTypes(servicesPayload);
  if (serviceLocationTypes.includes("both")) return "both";
  if (
    serviceLocationTypes.includes("at_location") &&
    serviceLocationTypes.includes("client_location")
  ) {
    return "both";
  }
  if (serviceLocationTypes.length > 0) return serviceLocationTypes[0];
  if (hasBusinessAddress(profilePayload)) return "at_location";
  return "";
};

const hasWorkLocationSetup = (profilePayload, servicesPayload) => {
  const workLocationType = inferWorkLocationType(profilePayload, servicesPayload);
  if (!workLocationType) {
    return false;
  }

  if (workLocationType === "client_location") {
    return true;
  }

  return hasBusinessAddress(profilePayload);
};

// The status endpoint now answers "not linked" with a 200; older API builds
// sent a 404 with this message, so it is still read as "no account yet".
const isStripeAccountNotLinkedError = (error) => {
  if (!error || typeof error.message !== "string") {
    return false;
  }

  return error.message.toLowerCase().includes("stripe account not linked");
};

const hasAvailability = (data) => {
  if (!data) return false;

  if (Array.isArray(data)) {
    return data.some((entry) => {
      const slots = Array.isArray(entry?.slots)
        ? entry.slots
        : Array.isArray(entry?.availability)
          ? entry.availability
          : [];
      return Array.isArray(slots) && slots.length > 0;
    });
  }

  if (Array.isArray(data?.availability)) {
    return data.availability.some((entry) => {
      if (!entry) return false;
      if (Array.isArray(entry.slots)) {
        return entry.slots.length > 0;
      }
      if (Array.isArray(entry.times)) {
        return entry.times.length > 0;
      }
      return false;
    });
  }

  if (Array.isArray(data?.data)) {
    return data.data.length > 0;
  }

  return false;
};

const hasServices = (data) => {
  if (!data) return false;

  if (Array.isArray(data)) {
    return data.length > 0;
  }

  if (Array.isArray(data?.services)) {
    return data.services.length > 0;
  }

  if (Array.isArray(data?.data)) {
    return data.data.length > 0;
  }

  return false;
};

const extractStripeRequirements = (data) => {
  if (!data || typeof data !== "object") {
    return [];
  }

  const nestedStatus =
    data.status && typeof data.status === "object" ? data.status : null;

  const candidates = [
    data.requirementsDue,
    data.requirements_due,
    data.currently_due,
    Array.isArray(data.requirements) ? data.requirements : null,
    data.requirements?.currently_due,
    nestedStatus?.requirementsDue,
    nestedStatus?.requirements_due,
    nestedStatus?.currently_due,
    Array.isArray(nestedStatus?.requirements) ? nestedStatus.requirements : null,
    nestedStatus?.requirements?.currently_due,
  ];

  return candidates.find((candidate) => Array.isArray(candidate)) || [];
};

const resolveStripeBoolean = (data, camelKey, snakeKey) => {
  const nestedStatus =
    data?.status && typeof data.status === "object" ? data.status : null;

  return normalizeBoolean(
    data?.[camelKey] ??
      data?.[snakeKey] ??
      nestedStatus?.[camelKey] ??
      nestedStatus?.[snakeKey]
  );
};

const isStripeComplete = (data) => {
  if (!data || typeof data !== "object") {
    return false;
  }

  const detailsSubmitted = resolveStripeBoolean(data, "detailsSubmitted", "details_submitted");
  const payoutsEnabled = resolveStripeBoolean(data, "payoutsEnabled", "payouts_enabled");
  const chargesEnabled = resolveStripeBoolean(data, "chargesEnabled", "charges_enabled");

  if (extractStripeRequirements(data).length > 0) {
    return false;
  }

  return detailsSubmitted && payoutsEnabled && chargesEnabled;
};

const resolveAppearanceValue = (payload, key) => {
  if (!payload || typeof payload !== "object") return "";

  const profilePayload = payload.professional ?? payload;

  const rawValue =
    profilePayload?.[key] ??
    profilePayload?.theme?.[key] ??
    profilePayload?.data?.[key] ??
    profilePayload?.profile?.[key] ??
    "";

  if (rawValue === null || rawValue === undefined) {
    return "";
  }

  return typeof rawValue === "string" ? rawValue.trim() : String(rawValue);
};

const hasAppearance = (payload) => {
  const appearanceValues = [
    resolveAppearanceValue(payload, "backgroundColor"),
    resolveAppearanceValue(payload, "textColor"),
    resolveAppearanceValue(payload, "fontFamily"),
  ];

  return appearanceValues.some((value) => typeof value === "string" && value.trim().length > 0);
};

const hasBookingQuestions = (payload) => {
  const profilePayload = payload?.professional ?? payload ?? {};
  const questions =
    profilePayload?.professionalQuestions ||
    payload?.professionalQuestions ||
    payload?.data?.professionalQuestions ||
    [];

  return Array.isArray(questions)
    ? questions.some((question) => question?.label && String(question.label).trim())
    : false;
};

const buildDefaultSteps = (userId, { teamSettingsRequired = false, activeBusinessId = null } = {}) => ({
  services: false,
  availability: false,
  payouts: false,
  payoutsSkipped: getStoredPayoutSkipCompletion(userId, activeBusinessId),
  shareProfile: getStoredShareCompletion(userId, activeBusinessId),
  profileGuidelines: false,
  profileDescription: false,
  workLocationSetup: false,
  appearance: false,
  bookingQuestions: false,
  teamSettings: false,
  teamSettingsDraftSaved: false,
  teamSettingsRequired,
  teamPaymentRequired: false,
  teamPaymentContext: null,
  businessTimeZone: null,
  canManageBusinessTimeZone: false,
  confirmedTimeZone: getStoredTimeZoneConfirmation(userId, activeBusinessId),
});

const fetchTeamModeOnboardingData = async ({ activeBusinessId, isBusinessOwner, signal }) => {
  const [routingPayload, teamPayload, onboardingPayload] = await Promise.all([
    authorizedRequest({
      url: `/api/businesses/${activeBusinessId}/payment-routing`,
      signal,
    }),
    authorizedRequest({
      url: `/api/team/businesses/${activeBusinessId}/team`,
      signal,
    }),
    isBusinessOwner
      ? authorizedRequest({
          url: `/api/businesses/${activeBusinessId}/team-onboarding`,
          signal,
        })
      : Promise.resolve({ teamOnboarding: null }),
  ]);

  const paymentRouting = routingPayload?.paymentRouting || {};
  const team = teamPayload || { members: [] };
  const payoutMode = paymentRouting.payoutMode || "business_managed";
  let payoutsComplete = false;
  let description = "Review your team's online-payment readiness.";

  if (payoutMode === "business_managed") {
    payoutsComplete = isBusinessOwner
      ? Boolean(paymentRouting.businessAccount?.ready)
      : true;
    description = isBusinessOwner
      ? "Connect the business Stripe account so Team Mode bookings can be paid online."
      : "Your business manages online booking payments through its Stripe account.";
  } else {
    const assignedProviderIds = new Set(
      (team.members || [])
        .filter((member) =>
          member.isProvider &&
          member.status === "active" &&
          (member.assignedServices || []).length > 0
        )
        .map((member) => String(member.user)),
    );
    const relevantProviders = (paymentRouting.providers || []).filter((provider) =>
      assignedProviderIds.has(String(provider.providerId)),
    );
    payoutsComplete = relevantProviders.length > 0 && relevantProviders.every(
      (provider) => provider.paymentStatus?.ready,
    );
    description = isBusinessOwner
      ? "Make sure every assigned provider can receive direct online payouts."
      : "Connect your provider Stripe account to receive direct online payouts.";
  }

  return {
    payoutsComplete,
    teamSettingsComplete: Boolean(onboardingPayload?.teamOnboarding?.completed),
    paymentContext: {
      payoutMode,
      title: payoutMode === "business_managed"
        ? "Connect business payments"
        : "Prepare provider payouts",
      description,
      ctaPath: payoutMode === "business_managed" || isBusinessOwner
        ? "/pro/settings?tab=payments"
        : "/payments/connect",
    },
    teamPaymentRequired: true,
    teamSettingsDraftSaved: Boolean(onboardingPayload?.teamOnboarding?.draftSaved),
  };
};

export const fetchOnboardingSteps = async (
  userId,
  {
    activeBusinessId = null,
    providerId = null,
    teamMode = false,
    businessRole = null,
    signal = undefined,
  } = {},
) => {
  const teamSettingsRequired = Boolean(
    teamMode && activeBusinessId && businessRole === "owner",
  );
  if (!userId) {
    return buildDefaultSteps(userId, { teamSettingsRequired, activeBusinessId });
  }

  const resolvedProviderId = providerId || userId;

  const teamDataPromise = teamMode && activeBusinessId
    ? fetchTeamModeOnboardingData({
      activeBusinessId,
      isBusinessOwner: businessRole === "owner",
      signal,
      })
    : Promise.resolve(null);
  const stripeStatusPromise = teamMode && activeBusinessId
    ? Promise.resolve(null)
    : authorizedRequest({
        url: activeBusinessId
          ? `/api/businesses/${activeBusinessId}/provider-connect/account-status`
          : "/api/payment/connect/account-status",
        signal,
      }).catch(
        (stripeError) => {
          if (isStripeAccountNotLinkedError(stripeError)) {
            return null;
          }
          throw stripeError;
        }
      );

  const [
    servicesResponse,
    availabilityResponse,
    stripeResponse,
    profileResponse,
    teamData,
    timeZoneSettings,
  ] = await Promise.all([
    authorizedRequest({
      url: activeBusinessId
        ? `/api/businesses/${activeBusinessId}/services`
        : "/api/services/my-services",
      signal,
    }),
    authorizedRequest({
      url: activeBusinessId
        ? `/api/businesses/${activeBusinessId}/providers/${resolvedProviderId}/availability`
        : `/api/availability/${resolvedProviderId}`,
      signal,
    }),
    stripeStatusPromise,
    authorizedRequest({
      url: activeBusinessId
        ? `/api/businesses/${activeBusinessId}/booking-profile`
        : "/api/professional/me/profile",
      signal,
    }),
    teamDataPromise,
    fetchBusinessTimeZoneSettings({ activeBusinessId, signal }),
  ]);

  const guidelinesValue = normalizeGuidelinesValue(profileResponse);
  const appearanceComplete = hasAppearance(profileResponse);
  const bookingQuestionsComplete = hasBookingQuestions(profileResponse);
  const profileDescriptionComplete = hasProfileDescription(profileResponse);
  const workLocationSetupComplete = hasWorkLocationSetup(
    profileResponse,
    servicesResponse,
  );
  const payoutsComplete = teamData
    ? teamData.payoutsComplete
    : isStripeComplete(stripeResponse);
  const teamPaymentRequired = Boolean(teamData?.teamPaymentRequired);
  const payoutsSkipped = payoutsComplete || teamPaymentRequired
    ? false
    : getStoredPayoutSkipCompletion(userId, activeBusinessId);

  return {
    ...buildDefaultSteps(userId, { teamSettingsRequired, activeBusinessId }),
    services: hasServices(servicesResponse),
    availability: hasAvailability(availabilityResponse),
    payouts: payoutsComplete,
    payoutsSkipped,
    profileGuidelines: guidelinesValue.length > 0,
    profileDescription: profileDescriptionComplete,
    workLocationSetup: workLocationSetupComplete,
    appearance: appearanceComplete,
    bookingQuestions: bookingQuestionsComplete,
    teamSettings: teamSettingsRequired ? teamData?.teamSettingsComplete === true : false,
    teamSettingsDraftSaved: teamSettingsRequired ? teamData?.teamSettingsDraftSaved === true : false,
    teamPaymentRequired,
    teamPaymentContext: teamData?.paymentContext || null,
    ...timeZoneSettings,
  };
};

export const calculateOnboardingProgress = (steps) => {
  const profileSetupComplete = Boolean(steps.profileDescription);
  const workLocationSetupComplete = Boolean(steps.workLocationSetup);
  const payoutsStepComplete = Boolean(
    steps.payouts || (!steps.teamPaymentRequired && steps.payoutsSkipped)
  );
  const completedCount = [
    steps.services,
    steps.availability,
    profileSetupComplete,
    workLocationSetupComplete,
    steps.bookingQuestions,
    steps.profileGuidelines,
    payoutsStepComplete,
    steps.shareProfile,
    ...(steps.teamSettingsRequired ? [steps.teamSettings] : []),
  ].filter(Boolean).length;

  const canShareProfile =
    steps.services && steps.availability && profileSetupComplete && workLocationSetupComplete;
  const totalSteps = steps.teamSettingsRequired
    ? TEAM_ONBOARDING_TOTAL_STEPS
    : ONBOARDING_TOTAL_STEPS;
  const progress = Math.round((completedCount / totalSteps) * 100);

  return {
    profileSetupComplete,
    workLocationSetupComplete,
    completedCount,
    totalSteps,
    canShareProfile,
    progress,
    isComplete: completedCount === totalSteps,
  };
};

// The navbar, the progress widget and the Overview all need onboarding status,
// and one check is four to six requests, one of them a live Stripe lookup.
// Callers asking for the same workspace at the same time share one check, and a
// finished onboarding is kept for the rest of the session so it is never
// re-checked. An unfinished one is not kept: the next caller checks again, so a
// step the pro just completed elsewhere shows up. A check that first finds the
// onboarding finished announces it, so the navbar drops its "Getting started"
// link without checking again itself.
const pendingOnboardingSteps = new Map();
const completedOnboardingSteps = new Map();

const buildOnboardingStepsKey = (
  userId,
  { activeBusinessId = null, providerId = null, teamMode = false, businessRole = null } = {},
) => JSON.stringify([
  userId,
  // The legacy (non-business) URLs are scoped by the active-workspace header.
  getStoredActiveBusinessId(),
  activeBusinessId,
  providerId || userId,
  Boolean(teamMode),
  businessRole,
]);

export const loadOnboardingSteps = (userId, options = {}) => {
  const key = buildOnboardingStepsKey(userId, options);
  if (completedOnboardingSteps.has(key)) {
    return Promise.resolve(completedOnboardingSteps.get(key));
  }
  if (pendingOnboardingSteps.has(key)) {
    return pendingOnboardingSteps.get(key);
  }

  // The check is shared, so no one caller's signal may cancel it.
  const request = fetchOnboardingSteps(userId, { ...options, signal: undefined })
    .then((steps) => {
      if (calculateOnboardingProgress(steps).isComplete) {
        completedOnboardingSteps.set(key, steps);
        if (userId && typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent(ONBOARDING_COMPLETED_EVENT, { detail: { userId } })
          );
        }
      }
      return steps;
    })
    .finally(() => {
      if (pendingOnboardingSteps.get(key) === request) {
        pendingOnboardingSteps.delete(key);
      }
    });
  pendingOnboardingSteps.set(key, request);
  return request;
};

// For a page to call after a save that can finish (or undo) a step. A check
// already under way may have read the data before the save landed, so the
// callers that re-check on this start a new one rather than share it.
export const notifyOnboardingStepsChanged = () => {
  pendingOnboardingSteps.clear();
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new CustomEvent(ONBOARDING_STEPS_CHANGED_EVENT));
};
